import { useRef, useState } from 'react';
import { useGraphQLQuery } from '@/api/hooks';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { PageHeader } from '@/components/primitives';
import { toast } from 'react-hot-toast';
import { Loader2, Play, FileCode, Copy } from 'lucide-react';

export function GraphQLRunnerPage() {
  const mutation = useGraphQLQuery();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState(`query getTemplates {
  listDashboardTemplates(limit: 10000) {
    items {
      id
      name
    }
  }
}`);
  const [variables, setVariables] = useState('{}');
  const [result, setResult] = useState<string>('');

  const handleRun = async () => {
    try {
      const parsedVars = JSON.parse(variables || '{}');
      const resp = await mutation.mutateAsync({ query, variables: parsedVars });
      setResult(JSON.stringify(resp, null, 2));
      toast.success('Query executed');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Query failed');
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target?.result as string;
      setQuery(text);
      toast.success(`Loaded ${file.name}`);
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  return (
    <div className="space-y-5 flex flex-col lg:h-[calc(100dvh-4rem)]">
      <PageHeader
        title="GraphQL Runner"
        description="Execute queries and mutations against the Mission Control AppSync API."
        actions={
          <>
            <input ref={fileInputRef} type="file" accept=".graphql" className="hidden" onChange={handleFileUpload} />
            <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}>
              <FileCode className="h-4 w-4 mr-2" />
              Load .graphql file
            </Button>
            <Button size="sm" onClick={handleRun} disabled={mutation.isPending}>
              {mutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Play className="h-4 w-4 mr-2" />
              )}
              Execute
            </Button>
          </>
        }
      />

      <div className="grid gap-4 lg:grid-cols-2 flex-1 min-h-0">
        <div className="flex flex-col gap-4 min-w-0 min-h-0">
          <Card className="p-0 overflow-hidden flex flex-col flex-1 min-h-0">
            <div className="px-3 py-2 border-b border-border text-[11px] font-semibold uppercase tracking-wide text-muted-foreground bg-muted/40">
              Query
            </div>
            <textarea
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="flex-1 w-full resize-none bg-transparent p-3 text-[13px] leading-relaxed font-mono focus:outline-none"
              spellCheck={false}
            />
          </Card>

          <Card className="p-0 overflow-hidden flex flex-col h-36 shrink-0">
            <div className="px-3 py-2 border-b border-border text-[11px] font-semibold uppercase tracking-wide text-muted-foreground bg-muted/40">
              Variables (JSON)
            </div>
            <textarea
              value={variables}
              onChange={(e) => setVariables(e.target.value)}
              className="flex-1 w-full resize-none bg-transparent p-3 text-[13px] leading-relaxed font-mono focus:outline-none"
              spellCheck={false}
            />
          </Card>
        </div>

        <Card className="p-0 overflow-hidden flex flex-col h-96 lg:h-full min-h-0 min-w-0">
          <div className="px-3 py-2 border-b border-border text-[11px] font-semibold uppercase tracking-wide text-muted-foreground bg-muted/40 flex justify-between items-center">
            <span>Result</span>
            {result && (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-[11px]"
                onClick={() => {
                  navigator.clipboard.writeText(result);
                  toast.success('Copied to clipboard');
                }}
              >
                <Copy className="h-3 w-3 mr-1" />
                Copy
              </Button>
            )}
          </div>
          <pre className="flex-1 overflow-auto p-3 text-xs font-mono leading-relaxed whitespace-pre-wrap">
            {result || <span className="text-muted-foreground">Run a query to see results</span>}
          </pre>
        </Card>
      </div>
    </div>
  );
}
